/**
 * PracticePulsePage — Practice Manager command center.
 *
 * Replaces the old /manager/overview (OverviewDashboard).
 * Sections:
 *   1. Topbar   — site selector, Customize (KPI config), Refresh
 *   2. Huddle Brief — AI-generated briefing (HuddleBanner)
 *   3. KPI Cards — 2×3 configurable grid (useUserPreferences)
 *   4. Inbox — AI-powered email client (replaces Operational Focus Tabs)
 *   5. Domain Dashboards — 2×4 accordion grid, single-open (DomainPanelGrid)
 */
import React, { useState, useEffect } from 'react';
import { Settings, Maximize2, Minimize2 } from 'lucide-react';
import HuddleBanner from './HuddleBanner';
import InboxShell from './inbox/InboxShell';
import DomainPanelGrid from './DomainPanelGrid';
import KPIConfigModal from './KPIConfigModal';
import { KPICard } from '../ui/KPICard';
import { SectionHeading } from '../ui/SectionHeading';
import { TooltipProvider } from '../ui/tooltip';
import { ICON_PX } from '../ui/lib/iconSizes';
import { useUserPreferences, DEFAULT_KPI_SELECTION } from '../../hooks/useUserPreferences';
import {
  fetchStaffingDashboard, getPMOverviewMetrics, fetchAccessMetrics,
  fetchQualityMetrics, fetchConsumerInsights, fetchFinancialOverview,
  fetchKPIs,
} from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { useConsumerUnit } from '../../contexts/ConsumerUnitContext';
import { useEventStream } from '../../hooks/useEventStream';

// ─── KPI display-format template ──────────────────────────────────────────────
// Labels and layout only — values show dashes until real API data arrives.
// Keys match KPIConfigModal / useUserPreferences ids.
const KPI_DATA = {
  'staffing.coverage':       { label: 'Staffing Coverage',    value: '—', trend: '', trendDown: false, alert: false, donut: null },
  'staffing.open_shifts':    { label: 'Open Shifts',          value: '—', trend: '', trendDown: false, alert: false, donut: null },
  'staffing.overtime':       { label: 'Overtime Hours',       value: '—', trend: '', trendDown: false, alert: false, donut: null },
  'staffing.turnover':       { label: 'Turnover Rate (30d)',  value: '—', trend: '', trendDown: false, alert: false, donut: null },
  'staffing.fill_rate':      { label: 'Fill Rate',            value: '—', trend: '', trendDown: false, alert: false, donut: null },
  'quality.task_completion': { label: 'Task Completion',      value: '—', trend: '', trendDown: false, alert: false, donut: null },
  'quality.performance':     { label: 'Quality Performance',  value: '—', trend: '', trendDown: false, alert: false, donut: null },
  'quality.a1c':             { label: 'A1C Control Rate',     value: '—', trend: '', trendDown: false, alert: false, donut: null },
  'quality.hedis':           { label: 'HEDIS Compliance',     value: '—', trend: '', trendDown: false, alert: false, donut: null },
  'quality.open_compliance': { label: 'Open Compliance',      value: '—', trend: '', trendDown: false, alert: false, donut: null },
  'access.same_day':         { label: 'Same-Day Access',      value: '—', trend: '', trendDown: false, alert: false, donut: null },
  'access.wait_time':        { label: 'Avg Wait Time',        value: '—', trend: '', trendDown: false, alert: false, donut: null },
  'access.open_slots':       { label: 'Open Slots Today',     value: '—', trend: '', trendDown: false, alert: false, donut: null },
  'access.backlog':          { label: 'Backlog Count',        value: '—', trend: '', trendDown: false, alert: false, donut: null },
  'access.utilization':      { label: 'Appt Utilization',    value: '—', trend: '', trendDown: false, alert: false, donut: null },
  'finance.revenue':         { label: 'Revenue/Patient',     value: '—', trend: '', trendDown: false, alert: false, donut: null },
  'finance.collections':     { label: 'Collections Rate',    value: '—', trend: '', trendDown: false, alert: false, donut: null },
  'finance.denials':         { label: 'Denied Claims',       value: '—', trend: '', trendDown: false, alert: false, donut: null },
  'finance.days_ar':         { label: 'Days in AR',          value: '—', trend: '', trendDown: false, alert: false, donut: null },
  'finance.variance':        { label: 'Budget Variance',     value: '—', trend: '', trendDown: false, alert: false, donut: null },
  'consumer.nps':            { label: 'Net Promoter Score',  value: '—', trend: '', trendDown: false, alert: false, donut: null },
  'consumer.satisfaction':   { label: 'Patient Satisfaction',value: '—', trend: '', trendDown: false, alert: false, donut: null },
  'consumer.complaints':     { label: 'Complaints (30d)',    value: '—', trend: '', trendDown: false, alert: false, donut: null },
  'consumer.resolved':       { label: 'Resolved Complaints', value: '—', trend: '', trendDown: false, alert: false, donut: null },
  'consumer.return_visits':  { label: 'Return Visit Rate',   value: '—', trend: '', trendDown: false, alert: false, donut: null },
  'practice.active_patients':{ label: 'Active Patients',     value: '—', trend: '', trendDown: false, alert: false, donut: null },
  'practice.new_patients':   { label: 'New Patients (MTD)',  value: '—', trend: '', trendDown: false, alert: false, donut: null },
  'practice.panel_util':     { label: 'Panel Utilization',   value: '—', trend: '', trendDown: false, alert: false, donut: null },
  'practice.no_show':        { label: 'No-Show Rate',        value: '—', trend: '', trendDown: false, alert: false, donut: null },
  'practice.referrals':      { label: 'Referral Completion', value: '—', trend: '', trendDown: false, alert: false, donut: null },
  'ops.intervention_time':   { label: 'Avg Intervention Time',value: '—', trend: '', trendDown: false, alert: false, donut: null },
  'ops.uptime':              { label: 'System Uptime',       value: '—', trend: '', trendDown: false, alert: false, donut: null },
  'ops.response_time':       { label: 'Response Time',       value: '—', trend: '', trendDown: false, alert: false, donut: null },
  'ops.escalation_rate':     { label: 'Escalation Rate',     value: '—', trend: '', trendDown: false, alert: false, donut: null },
  'ops.reliability_score':   { label: 'Reliability Score',   value: '—', trend: '', trendDown: false, alert: false, donut: null },
  'pophealth.outreach':      { label: 'Outreach Completion', value: '—', trend: '', trendDown: false, alert: false, donut: null },
  'pophealth.high_risk':     { label: 'High-Risk Pending',   value: '—', trend: '', trendDown: false, alert: false, donut: null },
  'pophealth.care_gaps':     { label: 'Care Gap Closure',    value: '—', trend: '', trendDown: false, alert: false, donut: null },
  'pophealth.care_plans':    { label: 'Active Care Plans',   value: '—', trend: '', trendDown: false, alert: false, donut: null },
  'pophealth.screenings':    { label: 'Screenings Due',      value: '—', trend: '', trendDown: false, alert: false, donut: null },
};

const FALLBACK_KPI = { label: 'KPI', value: '—', trend: null, trendDown: false, alert: false };

// ─── Main page ─────────────────────────────────────────────────────────────────
export default function PracticePulsePage() {
  const { prefs, savePreferences, error: prefsError } = useUserPreferences();
  const { getToken } = useAuth();
  const { selectedUnitId } = useConsumerUnit();
  const [showKPIConfig, setShowKPIConfig] = useState(false);
  const [focusExpanded, setFocusExpanded] = useState(false);
  const [liveKPI, setLiveKPI] = useState({});

  // Fetch live KPI data — unified endpoint replaces 6 separate domain calls
  useEffect(() => {
    let cancelled = false;

    async function loadKPIs() {
      try {
        const kpis = await fetchKPIs(null, selectedUnitId, getToken);
        if (cancelled) return;

        // If the unified API returned data, map to the liveKPI shape
        if (Array.isArray(kpis) && kpis.length > 0) {
          const overrides = {};
          kpis.forEach((k) => {
            const key = k.kpiKey || k.kpi_key;
            if (!key) return;
            const val = k.value;
            const target = k.target;
            const trendVal = k.trendValue ?? k.trend_value;
            const trendDir = k.trendDirection ?? k.trend_direction;
            const unit = k.unit || '%';

            // Format display value based on unit
            let displayValue;
            if (unit === '$') displayValue = `$${val}`;
            else if (unit === 'hours') displayValue = `${val}h`;
            else if (unit === 'days') displayValue = `${val}d`;
            else if (unit === '%') displayValue = `${val}%`;
            else if (unit === 'score') displayValue = String(val);
            else displayValue = val != null ? String(val) : '—';

            // Build trend text
            let trendText = '';
            if (trendVal != null && trendVal !== 0) {
              const sign = trendVal > 0 ? '+' : '';
              trendText = `${sign}${trendVal}${unit === '%' ? '%' : ''} vs prior`;
            } else {
              trendText = 'Stable';
            }

            const trendDown = trendDir === 'down' || (trendDir === 'up' && (
              key.includes('denials') || key.includes('complaints') ||
              key.includes('backlog') || key.includes('open_shifts') ||
              key.includes('high_risk') || key.includes('screenings') ||
              key.includes('no_show') || key.includes('escalation') ||
              key.includes('wait_time') || key.includes('intervention_time')
            ));
            const alert = k.status === 'critical' || k.status === 'below';

            overrides[key] = {
              label: k.label,
              value: displayValue,
              trend: trendText,
              trendDown,
              alert,
              donut: key === 'quality.task_completion' && val != null && target != null
                ? { value: val, total: target, color: '#2EB87A' }
                : null,
            };
          });
          setLiveKPI(overrides);
          return;
        }
      } catch {
        // Fall through to legacy multi-call approach
      }

      // Legacy fallback: 6 separate domain API calls
      if (cancelled) return;
      Promise.allSettled([
        fetchStaffingDashboard(selectedUnitId, getToken),
        getPMOverviewMetrics(selectedUnitId, getToken),
        fetchAccessMetrics(selectedUnitId, {}, getToken),
        fetchQualityMetrics(selectedUnitId, {}, getToken),
        fetchConsumerInsights(selectedUnitId, getToken),
        fetchFinancialOverview(selectedUnitId, getToken),
      ]).then(([staffRes, pmRes, accessRes, qualityRes, consumerRes, _financeRes]) => {
        if (cancelled) return;
        const overrides = {};

        if (staffRes.status === 'fulfilled' && staffRes.value) {
          const s = staffRes.value;
          if (s.coverageStatus) {
            overrides['staffing.coverage'] = {
              label: 'Staffing Coverage', value: `${s.coverageStatus.percentage}%`,
              trend: `${s.coverageStatus.trend > 0 ? '+' : ''}${s.coverageStatus.trend}% vs last week`,
              trendDown: s.coverageStatus.trend < 0, alert: s.coverageStatus.percentage < 90,
            };
          }
          if (s.openShifts) {
            overrides['staffing.open_shifts'] = {
              label: 'Open Shifts', value: String(s.openShifts.today),
              trend: `${s.openShifts.next7Days} in next 7d`, trendDown: s.openShifts.today > 0, alert: s.openShifts.today > 0,
            };
          }
          if (s.otExposure) {
            overrides['staffing.overtime'] = {
              label: 'Overtime Hours', value: `${s.otExposure.currentHours}h`,
              trend: `$${s.otExposure.projectedWeeklyCost.toLocaleString()} projected`,
              trendDown: false, alert: false,
            };
          }
        }

        if (pmRes.status === 'fulfilled' && pmRes.value) {
          const pm = pmRes.value;
          if (pm.completionRate) {
            overrides['quality.task_completion'] = {
              label: 'Task Completion', value: pm.completionRate.value,
              trend: `${pm.completionRate.value} of ${pm.completionRate.target} target`,
              trendDown: pm.completionRate.trend < 0, alert: pm.completionRate.value < pm.completionRate.target,
              donut: { value: pm.completionRate.value, total: pm.completionRate.target, color: '#2EB87A' },
            };
          }
        }

        if (accessRes.status === 'fulfilled' && accessRes.value) {
          const a = accessRes.value;
          if (a.sameDayRate != null) {
            overrides['access.same_day'] = {
              label: 'Same-Day Access', value: `${a.sameDayRate}%`,
              trend: a.sameDayRate < a.sameDayTarget ? `below ${a.sameDayTarget}% target` : 'On target',
              trendDown: a.sameDayRate < a.sameDayTarget, alert: a.sameDayRate < a.sameDayTarget,
            };
          }
          if (a.avgWaitDays != null) {
            overrides['access.wait_time'] = {
              label: 'Avg Wait Time', value: `${a.avgWaitDays}d`,
              trend: a.avgWaitDays > a.waitTarget ? `Above ${a.waitTarget}d target` : 'On target',
              trendDown: a.avgWaitDays > a.waitTarget, alert: false,
            };
          }
          if (a.openSlots != null) {
            overrides['access.open_slots'] = {
              label: 'Open Slots Today', value: String(a.openSlots),
              trend: `across ${a.providers} providers`, trendDown: false, alert: false,
            };
          }
          if (a.backlog != null) {
            overrides['access.backlog'] = {
              label: 'Backlog Count', value: String(a.backlog),
              trend: `Oldest: ${a.oldestRequest}d`, trendDown: false, alert: false,
            };
          }
        }

        if (qualityRes.status === 'fulfilled' && qualityRes.value) {
          const q = qualityRes.value;
          if (Array.isArray(q)) {
            const openCompliance = q.filter((m) => m.status === 'below');
            overrides['quality.open_compliance'] = {
              label: 'Open Compliance', value: String(openCompliance.length),
              trend: openCompliance.length > 0 ? 'Action needed' : 'On track',
              trendDown: openCompliance.length > 0, alert: openCompliance.length > 0,
            };
            const a1c = q.find((m) => m.name && m.name.toLowerCase().includes('a1c'));
            if (a1c) {
              overrides['quality.a1c'] = {
                label: 'A1C Control Rate', value: `${a1c.current}%`,
                trend: a1c.current < a1c.target ? `below ${a1c.target}% target` : 'On target',
                trendDown: a1c.trend === 'down', alert: a1c.current < a1c.target,
              };
            }
          }
        }

        if (consumerRes.status === 'fulfilled' && consumerRes.value) {
          const c = consumerRes.value;
          if (c.cpfiScore) {
            overrides['consumer.satisfaction'] = {
              label: 'Patient Satisfaction', value: `${c.cpfiScore.value}%`,
              trend: `${c.cpfiScore.trend > 0 ? '+' : ''}${c.cpfiScore.trend}%`,
              trendDown: c.cpfiScore.trend < 0, alert: c.cpfiScore.value < c.cpfiScore.target,
            };
          }
          if (c.openComplaints) {
            overrides['consumer.complaints'] = {
              label: 'Complaints (30d)', value: String(c.openComplaints.total),
              trend: `${c.openComplaints.aging.over7d} aging 7d+`,
              trendDown: c.openComplaints.total > 5, alert: false,
            };
          }
        }

        setLiveKPI(overrides);
      });
    }

    loadKPIs();
    return () => { cancelled = true; };
  }, [getToken, selectedUnitId]);

  // SSE: live KPI updates — merge incoming domain KPIs into liveKPI state
  const { subscribe } = useEventStream();
  useEffect(() => {
    const unsub = subscribe('kpi.updated', (data) => {
      if (!data.kpis || !Array.isArray(data.kpis)) return;
      setLiveKPI((prev) => {
        const next = { ...prev };
        data.kpis.forEach((k) => {
          const key = k.key;
          if (!key) return;
          next[key] = {
            ...(prev[key] || {}),
            value: k.value != null ? String(k.value) : '—',
            alert: k.status === 'critical' || k.status === 'below',
          };
        });
        return next;
      });
    });
    return unsub;
  }, [subscribe]);

  const selectedKPIs = prefs.kpi_config?.selected ?? DEFAULT_KPI_SELECTION;

  return (
    <TooltipProvider delayDuration={200}>
    <div
      className="dashboard-container"
      style={{
        display: 'flex', flexDirection: 'column',
        gap: 8,
        overflowY: focusExpanded ? 'hidden' : 'auto',
        position: 'relative',
      }}
    >

      {/* ── Huddle Banner ───────────────────────────────────────────────── */}
      <div style={{ flexShrink: 0 }}>
        <HuddleBanner />
      </div>

      {/* ── Key Indicators — horizontal strip ─────────────────────────── */}
      <div style={{ flexShrink: 0 }}>
        <SectionHeading
          title="Key Indicators"
          size="sm"
          className="mb-1.5"
          info="Your top 6 configurable KPIs across all domains. Click the gear icon to choose which metrics appear here."
          aiContext="practice-pulse.kpi-grid"
          action={
            <button
              type="button"
              onClick={() => setShowKPIConfig(true)}
              title="Customize KPIs"
              style={{
                display: 'flex', alignItems: 'center',
                color: 'var(--text-dark-secondary)',
                background: 'none', border: '1px solid var(--surface)',
                borderRadius: 6, padding: '3px 6px', cursor: 'pointer',
              }}
            >
              <Settings size={ICON_PX.md} />
            </button>
          }
        />
        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${selectedKPIs.slice(0, 6).length}, 1fr)`,
          gap: 8,
        }}>
          {selectedKPIs.slice(0, 6).map((id) => {
            const kpi = liveKPI[id] || KPI_DATA[id] || FALLBACK_KPI;
            return (
              <KPICard
                key={id}
                label={kpi.label}
                value={String(kpi.value)}
                trend={kpi.trend ? kpi.trend.replace(/^[↑↓]\s*/, '') : undefined}
                trendDown={kpi.trendDown}
                domain={id.split('.')[0]}
                entityId={id}
                accentPlacement="top"
                size="sm"
                density="compact"
              />
            );
          })}
        </div>
      </div>

      {/* ── Operational Focus — full-width ─────────────────────────────── */}
      <div
        style={{
          flexShrink: 0, minWidth: 0,
          display: 'flex', flexDirection: 'column',
          height: focusExpanded ? undefined : 520,
          ...(focusExpanded && {
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            zIndex: 10, background: 'var(--surface)',
            height: 'auto',
          }),
        }}
      >
        <SectionHeading
          title="Operational Focus"
          size="sm"
          className="mb-1.5"
          info="Your operational command center. Triage emails, manage your priority queue, organize work on the kanban board, or view scheduled items on the calendar."
          action={
            <button
              type="button"
              onClick={() => setFocusExpanded((v) => !v)}
              title={focusExpanded ? 'Collapse' : 'Expand'}
              style={{
                background: 'none', border: '1px solid var(--surface)',
                borderRadius: 6, padding: '3px 6px', cursor: 'pointer',
                color: 'var(--text-dark-secondary)', display: 'flex', alignItems: 'center',
              }}
            >
              {focusExpanded ? <Minimize2 size={ICON_PX.md} /> : <Maximize2 size={ICON_PX.md} />}
            </button>
          }
        />
        <InboxShell
          style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
          expanded={focusExpanded}
          variant="smart"
        />
      </div>

      {/* ── Domain Dashboards — 2×4 accordion grid ──────────────────────── */}
      <DomainPanelGrid />

      {/* ── KPI Config Modal ────────────────────────────────────────────── */}
      {showKPIConfig && (
        <KPIConfigModal
          initialSelected={selectedKPIs}
          onSave={(newSelected) => savePreferences({ kpi_config: { selected: newSelected } })}
          onClose={() => setShowKPIConfig(false)}
          saveError={prefsError}
        />
      )}
    </div>
    </TooltipProvider>
  );
}
